import { useState } from "react";
import { Settings as Cog, User, Link2, Shield, LogOut, ExternalLink, Wifi, Type } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn } from "@/components/common";
import { TOKEN_KEY } from "@/lib/api";
import { getFontStyle, applyFontStyle } from "@/lib/appearance";

export default function Settings() {
  const { user, logout } = useAuth();
  const hasToken = !!(localStorage.getItem(TOKEN_KEY) || localStorage.getItem("token"));
  const [fontStyle, setFontStyle] = useState(getFontStyle());
  const chooseFont = (s) => { applyFontStyle(s); setFontStyle(s); };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Account and integration preferences" icon={Cog} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-3xl p-6 lg:col-span-2" data-testid="appearance-card">
          <div className="flex items-center gap-2 text-violet-300 mb-1"><Type size={18} /><h2 className="font-display text-lg">Appearance</h2></div>
          <p className="text-sm text-zinc-500 mb-4">Pick the font style used across the portal.</p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {[["bold", "Bold", "Modern & heavy"], ["classic", "Classic", "Fantasy serif"]].map(([k, label, desc]) => (
              <button
                key={k}
                data-testid={`font-style-${k}`}
                onClick={() => chooseFont(k)}
                className={`rounded-2xl p-4 border text-left transition ${fontStyle === k ? "border-violet-500/60 bg-violet-500/10" : "border-white/10 hover:border-white/20"}`}
              >
                <div className="text-2xl text-white" style={k === "bold" ? { fontFamily: "Outfit, system-ui, sans-serif", fontWeight: 800, letterSpacing: "-0.015em" } : { fontFamily: "Cinzel, serif", fontWeight: 700 }}>Aa</div>
                <div className="text-sm font-semibold text-white mt-2">{label}{fontStyle === k && <span className="text-[11px] text-violet-300 ml-2">Active</span>}</div>
                <div className="text-xs text-zinc-500">{desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 text-violet-300 mb-4"><User size={18} /><h2 className="font-display text-lg">Account</h2></div>
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-violet-800 flex items-center justify-center font-display text-2xl font-bold">
              {(user?.username || "A")[0].toUpperCase()}
            </div>
            <div>
              <div className="font-display text-xl text-white">{user?.username}</div>
              <div className="text-sm text-zinc-500">{user?.email}</div>
              <span className="inline-block mt-1 text-xs px-2.5 py-0.5 rounded-full bg-violet-500/15 text-violet-300 capitalize">{user?.role}</span>
            </div>
          </div>
          <Btn variant="danger" onClick={logout} data-testid="settings-logout"><LogOut size={16} /> Sign Out</Btn>
        </div>

        {user?.role === "admin" && (
        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 text-teal-300 mb-4"><Link2 size={18} /><h2 className="font-display text-lg">BeyondTheTable Integration</h2></div>
          <div className="space-y-3 text-sm text-zinc-400">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${hasToken ? "bg-teal-400" : "bg-zinc-600"}`} />
              {hasToken ? "Shared session token active" : "Standalone session"}
            </div>
            <p className="leading-relaxed">
              Launch this portal from BeyondTheTable with a shared JWT and you'll be signed in automatically:
            </p>
            <code className="block bg-black/40 border border-white/[0.06] rounded-xl p-3 text-xs text-violet-200 font-mono break-all">
              {window.location.origin}/?token=YOUR_JWT
            </code>
            <p className="text-xs text-zinc-600">
              Set the backend <span className="text-zinc-400">JWT_SECRET</span> to match BeyondTheTable so tokens validate across both apps.
            </p>
          </div>
        </div>
        )}

        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 text-amber-300 mb-4"><Wifi size={18} /><h2 className="font-display text-lg">Offline & Caching</h2></div>
          <p className="text-sm text-zinc-400 leading-relaxed">
            This portal is a PWA — install it to your device and it caches the app shell and SRD data for fast, offline-friendly access. Data syncs automatically when you're back online.
          </p>
        </div>

        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 text-violet-300 mb-4"><Shield size={18} /><h2 className="font-display text-lg">Data Sources</h2></div>
          <p className="text-sm text-zinc-400 leading-relaxed mb-3">Spells, items and conditions are sourced from the 5e SRD (OGL).</p>
          <div className="flex flex-wrap gap-2">
            <a href="https://www.dnd5eapi.co" target="_blank" rel="noreferrer" className="text-xs glass glass-hover rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-zinc-300">D&D 5e API <ExternalLink size={12} /></a>
            <a href="https://open5e.com" target="_blank" rel="noreferrer" className="text-xs glass glass-hover rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-zinc-300">Open5e <ExternalLink size={12} /></a>
          </div>
        </div>
      </div>
    </div>
  );
}
